// src/services/tokenFetcher/index.js

import { supportedChains } from '../../chains';
import { fetchTokens as fetchBitrockTokens } from './bitrockTokenFetcher';
import { fetchTokens as fetchEthereumTokens } from './ethereumTokenFetcher';
import { fetchTokens as fetchBaseTokens } from './baseTokenFetcher';
import { fetchTokens as fetchArbitrumTokens } from './arbitrumTokenFetcher';
import { fetchTokens as fetchAvalancheTokens } from './avalancheTokenFetcher';
import { fetchTokens as fetchPolygonTokens } from './polygonTokenFetcher';
import { fetchTokens as fetchCeloTokens } from './celoTokenFetcher';
import { fetchTokens as fetchOptimismTokens } from './optimismTokenFetcher';
import { fetchTokens as fetchCronosTokens } from './cronosTokenFetcher';
import { fetchTokens as fetchOmaxTokens } from './omaxTokenFetcher';
import { fetchTokens as fetchInfinaeonTokens } from './infinaeonTokenFetcher';
import { fetchTokens as fetchTaraxaTokens } from './taraxaTokenFetcher';
import { fetchTokens as fetchBscTokens } from './bscTokenFetcher';

import { standardizeTokenFormat } from './tokenFormatUtils';

// Map of chain keys to their specific token fetcher implementations
const TOKEN_FETCHERS = {
  'bitrock': fetchBitrockTokens,
  'ethereum': fetchEthereumTokens,
  'base': fetchBaseTokens,
  'arbitrum': fetchArbitrumTokens,
  'avalanche': fetchAvalancheTokens,
  'polygon': fetchPolygonTokens,
  'celo': fetchCeloTokens,
  'optimism': fetchOptimismTokens,
  'cronos': fetchCronosTokens,
  'omax': fetchOmaxTokens,
  'infinaeon': fetchInfinaeonTokens,
  'taraxa': fetchTaraxaTokens,
  'bsc': fetchBscTokens,

  // Add new chains and their fetchers here
};

/**
 * Generic token fetcher implementation suitable for chains 
 * that follow the ERC-20 contract standard but might have
 * different explorer APIs
 */
const fetchGenericTokens = async (walletAddress, apiEndpoint, responseMapper) => {
  try {
    // Replace {address} placeholder in API endpoint
    const url = apiEndpoint.replace('{address}', walletAddress);
    
    const response = await fetch(url);
    const data = await response.json();
    
    // Use the provided mapper function to transform the API response
    return responseMapper(data);
  } catch (error) {
    console.error("Error fetching tokens:", error);
    return [];
  }
};

/**
 * Fetch tokens for a given wallet address based on the chain.
 * This function handles chain-specific token fetching logic
 * and will work with any chain that has the appropriate configuration.
 *
 * @param {string} chainKey - The key of the chain (e.g., 'bitrock' or 'ethereum')
 * @param {string} walletAddress - The wallet address for which to fetch tokens
 * @returns {Promise<Array>} - A promise that resolves to an array of tokens
 */
export const fetchTokens = async (chainKey, walletAddress) => {
  // First check if we have a custom fetcher implementation for this chain
  if (TOKEN_FETCHERS[chainKey]) {
    return TOKEN_FETCHERS[chainKey](walletAddress);
  }
  
  // For chains without a custom fetcher, check if they have a tokenApiEndpoint
  const chainConfig = supportedChains[chainKey];
  if (!chainConfig) {
    throw new Error(`Unsupported chain: ${chainKey}`);
  }
  
  // Check if this chain has a tokenApiEndpoint and responseMapper
  if (chainConfig.tokenApiEndpoint && chainConfig.tokenResponseMapper) {
    return fetchGenericTokens(
      walletAddress, 
      chainConfig.tokenApiEndpoint,
      chainConfig.tokenResponseMapper
    );
  }
  
  // If no custom fetcher or generic API endpoint is available
  console.warn(`No token fetcher defined for chain: ${chainKey}`);
  return [];
};

/**
 * Register a new token fetcher implementation for a specific chain
 * 
 * @param {string} chainKey - The key of the chain
 * @param {function} fetcherFunction - The token fetcher implementation
 */
export const registerTokenFetcher = (chainKey, fetcherFunction) => {
  if (typeof fetcherFunction !== 'function') {
    throw new Error('fetcherFunction must be a function');
  }
  
  TOKEN_FETCHERS[chainKey] = fetcherFunction;
};